import React, { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../../contexts/LanguageContext';

const VideoRecorder = ({ onRecordingComplete, questionId, decodedInfo }) => {
  const { t } = useTranslation('applicants');
  const { isRTL, language } = useLanguage();
  const [isRecording, setIsRecording] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [videoURL, setVideoURL] = useState('');
  const [elapsedTime, setElapsedTime] = useState(0);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadComplete, setUploadComplete] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const videoRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const recordedChunksRef = useRef([]);
  const timerRef = useRef(null);
  const uploadTaskRef = useRef(null);

  const startTimer = () => {
    timerRef.current = setInterval(() => {
      setElapsedTime(prev => prev + 1);
    }, 1000);
  };

  const stopTimer = () => {
    clearInterval(timerRef.current);
  };

  const handleStartRecording = async () => {
    setVideoURL('');
    setElapsedTime(0);
    try {
      // 🚀 OPTIMIZED: 480p default with network-friendly settings for 3-min videos
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          width: { ideal: 854, max: 854 },    // 480p width
          height: { ideal: 480, max: 480 },   // 480p height
          frameRate: { ideal: 15, max: 20 }   // Lower FPS = smaller files
        },
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
          sampleRate: 22050  // Lower sample rate for smaller audio
        }
      });
      videoRef.current.srcObject = stream;
      videoRef.current.play();

      // 🎯 AGGRESSIVE COMPRESSION: Optimized for 3-min videos on slow networks
      const compressionOptions = {
        mimeType: 'video/webm;codecs=vp9,opus',
        videoBitsPerSecond: 800000,  // 800 kbps (vs 8+ Mbps default) = ~18MB for 3min
        audioBitsPerSecond: 64000    // 64 kbps audio (vs 128 kbps) = smaller files
      };

      // 🔄 FALLBACK: Support for different browsers
      if (!MediaRecorder.isTypeSupported(compressionOptions.mimeType)) {
        compressionOptions.mimeType = 'video/webm;codecs=vp8,opus';
        compressionOptions.videoBitsPerSecond = 1000000; // Slightly higher for VP8

        if (!MediaRecorder.isTypeSupported(compressionOptions.mimeType)) {
          compressionOptions.mimeType = 'video/webm';
          compressionOptions.videoBitsPerSecond = 1200000; // Basic WebM
        }
      }

      console.log('🎬 Recording with compression:', compressionOptions);
      mediaRecorderRef.current = new MediaRecorder(stream, compressionOptions);
      recordedChunksRef.current = [];

      mediaRecorderRef.current.ondataavailable = (event) => {
        if (event.data.size > 0) {
          recordedChunksRef.current.push(event.data);
        }
      };

      mediaRecorderRef.current.onstop = () => {
        const videoBlob = new Blob(recordedChunksRef.current, { type: 'video/webm' });
        const fileSizeMB = (videoBlob.size / 1024 / 1024).toFixed(2);
        console.log(`🎯 OPTIMIZED Video: ${fileSizeMB} MB (vs ~100MB+ uncompressed)`);

        const url = URL.createObjectURL(videoBlob);
        setVideoURL(url);

        // Immediately start optimized upload process
        handleOptimizedUpload(videoBlob);

        stream.getTracks().forEach(track => track.stop());
      };

      mediaRecorderRef.current.start();
      setIsRecording(true);
      startTimer();
    } catch (err) {
      console.error("Error accessing media devices.", err);
      alert(t('assessmentViewer.recorder.permissionError'));
    }
  };

  const handleStopRecording = () => {
    mediaRecorderRef.current.stop();
    setIsRecording(false);
    setIsPaused(false);
    stopTimer();
  };

  const handlePauseResume = () => {
    if (isPaused) {
      mediaRecorderRef.current.resume();
      startTimer();
    } else {
      mediaRecorderRef.current.pause();
      stopTimer();
    }
    setIsPaused(!isPaused);
  };

  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const handleOptimizedUpload = async (videoBlob) => {
    if (!decodedInfo) {
      setUploadError('Missing upload configuration');
      return;
    }

    try {
      setIsUploading(true);
      setUploadProgress(0);
      setUploadError('');

      const { companyId, applicantId, assessmentId } = decodedInfo;

      // Use correct connection key pattern for file uploads
      let connectionKey;
      if (applicantId.startsWith('temp_')) {
        connectionKey = `${companyId}_imported_${applicantId}`;
      } else {
        connectionKey = `${companyId}_${applicantId}`;
      }

      const fileName = `video_${questionId}_${Date.now()}.webm`;

      // Check if we need to use backend API (no Firebase auth available)
      // For video recordings in assessments, we assume candidates are unauthenticated
      // and always use the backend API for safety

      console.log('DEBUG: Uploading video via backend API (candidate mode)');

      // Convert blob to base64
      const base64Data = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          const base64 = reader.result.split(',')[1];
          resolve(base64);
        };
        reader.onerror = reject;
        reader.readAsDataURL(videoBlob);
      });

      // Create the slug from decodedInfo (URL-safe base64)
      const rawSlug = btoa(`${companyId}_${assessmentId}_${applicantId}`);
      const slug = rawSlug.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');

      setUploadProgress(10); // Show initial progress

      const response = await fetch(`${BACKEND_URL}/upload-assessment-file`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          slug: slug,
          question_id: questionId,
          file_name: fileName,
          file_data: base64Data,
          content_type: 'video/webm',
          transcription_language: language
        })
      });

      setUploadProgress(80); // Show near completion

      const result = await response.json();

      if (!result.success) {
        throw new Error(result.error || 'Upload failed');
      }

      const downloadURL = result.download_url;
      console.log('🎯 Video uploaded successfully via backend:', downloadURL);

      setUploadProgress(100);

      // 🎤 CRITICAL: Immediately trigger speech-to-text transcription
      await triggerBackgroundTranscription(downloadURL, questionId);

      // 📤 Call parent callback with the optimized video URL
      onRecordingComplete(questionId, downloadURL);

      setUploadComplete(true);
      setIsUploading(false);

    } catch (error) {
      console.error('Upload failed:', error);
      setUploadError(t('assessmentViewer.recorder.uploadFailed'));
      setIsUploading(false);
    }
  };

  const triggerBackgroundTranscription = async (videoUrl, questionId) => {
    try {
      console.log('🎤 STARTING transcription for optimized video:', questionId);

      // 🚀 ENHANCED: Call backend with optimized video metadata
      const response = await fetch(`${BACKEND_URL}/transcribe-video-async`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          videoUrl: videoUrl,
          questionId: questionId,
          companyId: decodedInfo?.companyId,
          assessmentId: decodedInfo?.assessmentId,
          applicantId: decodedInfo?.applicantId,
          transcriptionLanguage: language,
          // 🎯 SPEECH-TO-TEXT: Metadata for optimized processing
          videoMetadata: {
            resolution: '480p',
            codec: 'webm',
            audioQuality: '64kbps',
            optimized: true,
            transcriptionPriority: 'high'
          }
        })
      });

      const result = await response.json().catch(() => ({}));
      if (response.ok && result.success) {
        console.log('✅ Background transcription completed successfully for:', questionId);
      } else {
        console.warn(
          '⚠️ Transcription request failed but upload succeeded:',
          result.error || result.detail || `HTTP ${response.status}`
        );
      }
    } catch (error) {
      console.error('❌ Failed to trigger transcription:', error);
      // 🔄 Don't fail upload if transcription fails - evaluator will retry
      console.log('📝 Note: Transcription will be attempted during evaluation if needed');
    }
  };

  const cancelUpload = () => {
    if (uploadTaskRef.current) {
      uploadTaskRef.current.cancel();
      setIsUploading(false);
      setUploadProgress(0);
      setUploadError('Upload cancelled');
    }
  };

  return (
    <div style={{
      border: '1px solid #ddd',
      borderRadius: '8px',
      padding: '15px',
      backgroundColor: '#f9f9f9',
      textAlign: 'center'
    }}>
      <div style={{
        position: 'relative',
        width: '100%',
        paddingTop: '56.25%', // 16:9 Aspect Ratio
        backgroundColor: '#000',
        borderRadius: '8px',
        overflow: 'hidden',
        marginBottom: '15px'
      }}>
        <video
          ref={videoRef}
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover'
          }}
          muted
        />
        {isRecording && (
          <div style={{
            position: 'absolute',
            top: '10px',
            right: isRTL ? 'auto' : '10px',
            left: isRTL ? '10px' : 'auto',
            background: 'rgba(0,0,0,0.5)',
            color: '#fff',
            padding: '5px 10px',
            borderRadius: '4px',
            fontSize: '14px',
            display: 'flex',
            alignItems: 'center',
            flexDirection: isRTL ? 'row-reverse' : 'row',
            gap: '8px'
          }}>
            <i className="fa-solid fa-circle" style={{ color: 'var(--app-tone-f44336, #f44336)', fontSize: '10px' }}></i>
            {formatTime(elapsedTime)}
          </div>
        )}
      </div>

      <div style={{ display: 'flex', justifyContent: 'center', gap: '10px', flexWrap: 'wrap', flexDirection: isRTL ? 'row-reverse' : 'row' }}>
        {!isRecording && !isUploading ? (
          <button onClick={handleStartRecording} style={controlButtonStyle}>
            <i className="fa-solid fa-video" style={{ marginInlineEnd: '8px' }}></i>
            {t('assessmentViewer.recorder.start')}
          </button>
        ) : isRecording ? (
          <>
            <button onClick={handlePauseResume} style={controlButtonStyle}>
              <i className={`fa-solid ${isPaused ? 'fa-play' : 'fa-pause'}`} style={{ marginInlineEnd: '8px' }}></i>
              {isPaused ? t('assessmentViewer.recorder.resume') : t('assessmentViewer.recorder.pause')}
            </button>
            <button onClick={handleStopRecording} style={{ '--light-control-fill': '#f44336', ...controlButtonStyle, background: '#f44336' }}  data-light-control={"filled"}>
              <i className="fa-solid fa-stop" style={{ marginInlineEnd: '8px' }}></i>
              {t('assessmentViewer.recorder.stop')}
            </button>
          </>
        ) : isUploading ? (
          <>
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '8px',
              padding: '15px 20px',
              backgroundColor: '#f8f9fa',
              borderRadius: '8px',
              minWidth: '280px'
            }}>
              {/* 🚀 ENHANCED: Show optimization info */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexDirection: isRTL ? 'row-reverse' : 'row' }}>
                <i className="fa-solid fa-compress-alt" style={{ color: 'var(--app-tone-28a745, #28a745)', fontSize: '16px' }}></i>
                <span style={{ fontSize: '12px', color: 'var(--app-tone-28a745, #28a745)', fontWeight: "var(--app-weight-strong, bold)" }}>
                  {t('assessmentViewer.recorder.optimized')}
                </span>
              </div>

              {/* Upload progress */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexDirection: isRTL ? 'row-reverse' : 'row' }}>
                <i className="fa-solid fa-cloud-upload-alt" style={{ color: '#568ded' }}></i>
                <span style={{ fontSize: '14px', color: "var(--app-neutral-ink, #333)", fontWeight: '500' }}>
                  {t('assessmentViewer.recorder.uploadingWithProgress', { progress: uploadProgress })}
                </span>
                <div style={{
                  width: '120px',
                  height: '6px',
                  backgroundColor: '#e0e0e0',
                  borderRadius: '3px',
                  overflow: 'hidden'
                }}>
                  <div style={{
                    width: `${uploadProgress}%`,
                    height: '100%',
                    backgroundColor: uploadProgress > 90 ? '#28a745' : '#568ded',
                    transition: 'width 0.3s ease'
                  }}></div>
                </div>
              </div>

              {/* Network-friendly message */}
              <div style={{ fontSize: '11px', color: '#6c757d', fontStyle: 'italic', display: 'flex', alignItems: 'center', gap: '4px', flexDirection: isRTL ? 'row-reverse' : 'row' }}>
                <i className="fa-solid fa-wifi"></i>
                {t('assessmentViewer.recorder.networkNote')}
              </div>
            </div>
            <button onClick={cancelUpload} style={{ ...controlButtonStyle, background: 'var(--app-surface, #6c757d)' }}  data-light-control={"quiet"}>
              <i className="fa-solid fa-times" style={{ marginInlineEnd: '8px' }}></i>
              {t('assessmentViewer.recorder.cancel')}
            </button>
          </>
        ) : null}
      </div>

      {/* Upload Status Messages */}
      {uploadError && (
        <div style={{
          marginTop: '10px',
          padding: '10px',
          backgroundColor: '#f8d7da',
          border: '1px solid #f5c6cb',
          borderRadius: '4px',
          color: 'var(--app-tone-721c24, #721c24)',
          fontSize: '14px',
          textAlign: 'center'
        }}>
          <i className="fa-solid fa-exclamation-triangle" style={{ marginRight: '8px' }}></i>
          {uploadError}
        </div>
      )}

      {uploadComplete && (
        <div style={{
          marginTop: '10px',
          padding: '12px 15px',
          backgroundColor: '#d4edda',
          border: '1px solid #c3e6cb',
          borderRadius: '8px',
          color: 'var(--app-tone-155724, #155724)',
          fontSize: '14px',
          textAlign: 'center'
        }}>
          <div style={{ marginBottom: '5px' }}>
            <i className="fa-solid fa-check-circle" style={{ marginRight: '8px', fontSize: '16px' }}></i>
            <strong>{t('assessmentViewer.recorder.uploadSuccess')}</strong>
          </div>
        </div>
      )}

      {videoURL && !isUploading && (
        <div style={{ marginTop: '15px' }}>
          <h4 style={{ margin: '0 0 10px 0', color: "var(--app-neutral-ink, #333)" }}>{t('assessmentViewer.recorder.preview')}</h4>
          <video src={videoURL} controls style={{ width: '100%', borderRadius: '8px' }} />
          {!uploadComplete && (
            <p style={{ fontSize: '12px', color: '#666', textAlign: 'center', marginTop: '8px' }}>
              <i className="fa-solid fa-info-circle" style={{ marginInlineEnd: '5px' }}></i>
              {t('assessmentViewer.recorder.autoUpload')}
            </p>
          )}
        </div>
      )}
    </div>
  );
};

const controlButtonStyle = {
  background: '#568ded',
  color: '#fff',
  border: 'none',
  borderRadius: '8px',
  padding: '10px 20px',
  cursor: 'pointer',
  fontSize: '16px',
  fontFamily: 'Montserrat',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center'
};

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL_PROD;

export default VideoRecorder; 
